import { Box, Stack } from '@mui/material';
import { parseISO, addDays } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { MilestoneDto } from 'tdc-web-backend/milestones/schemas';
import { useTranslation } from 'react-i18next';
import { primaryDark, secondaryBlue } from '../../../../../utils/color';
import { simpleDateFormatter } from '../../../../../utils/helpers';
import CustomButton from '../../../../../components/button/CustomButton';
import AddMilestoneModal from './components/AddMilestoneModal';
import MilestoneItem from './components/MilestoneItem';

const MilestonesForm = ({ disabled = false }: { disabled?: boolean }) => {
  const { control, getValues, setValue, watch, trigger } = useFormContext();

  const { t } = useTranslation();
  const { fields, remove, append } = useFieldArray({
    control,
    name: 'body.milestones',
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Milestone display inherited value converter
  const updateBudget = () => {
    const milestones = getValues('body.milestones');
    const totalBudget: number = milestones.reduce((acc: number, milestone: MilestoneDto) => {
      if (milestone.budget === null) return acc;
      return acc + milestone.budget;
    }, 0);

    setValue(`initialAmount`, totalBudget);
  };

  const removeMilestone = (index: number) => {
    remove(index);

    // Force dirty check for body.milestones to update formState in ContractEditor
    const currentMilestones = getValues('body.milestones');
    setValue('body.milestones', currentMilestones, {
      shouldDirty: true,
      shouldValidate: true,
    });

    updateBudget();
  };

  const setEndDateForRecurring = (index: number, value: number) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const startDate = parseISO(getValues(`body.milestones.${index}.start`) as any);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any,no-restricted-globals
    if (!isNaN((startDate as any).getTime())) {
      startDate.setMonth(startDate.getMonth() + value);
      setValue(`body.milestones.${index}.end`, startDate.toISOString());
    }
  };

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name) {
        const milestones = getValues('body.milestones');

        // Update projectDurationFrom and projectDurationTo on any milestone start/end change
        if (
          name.startsWith('body.milestones.') &&
          (name.endsWith('.start') || name.endsWith('.end'))
        ) {
          // Calculate min start date and max end date
          let minStartDate: Date | null = null;
          let maxEndDate: Date | null = null;

          milestones.forEach((milestone: MilestoneDto) => {
            if (milestone.start) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const startDate =
                typeof (milestone as any).start === 'string'
                  ? parseISO((milestone as any).start)
                  : (milestone as any).start;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any,no-restricted-globals
              if (!isNaN((startDate as any).getTime())) {
                if (!minStartDate || startDate < minStartDate) {
                  minStartDate = startDate;
                }
              }
            }
            if (milestone.end) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const endDate =
                typeof (milestone as any).end === 'string'
                  ? parseISO((milestone as any).end)
                  : (milestone as any).end;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any,no-restricted-globals
              if (!isNaN((endDate as any).getTime())) {
                if (!maxEndDate || endDate > maxEndDate) {
                  maxEndDate = endDate;
                }
              }
            }
          });

          if (minStartDate) {
            setValue('body.projectDurationFrom', simpleDateFormatter(minStartDate));
          }

          if (maxEndDate) {
            setValue('body.projectDurationTo', simpleDateFormatter(maxEndDate));
          }
        }

        if (name.startsWith('body.milestones.') && name.endsWith('.duration')) {
          const parts = name.split('.');
          const index = +parts[2];
          const { duration } = value.body.milestones[index];
          setEndDateForRecurring(index, duration);
        }
        if (name.startsWith('body.milestones.') && name.endsWith('.recurring')) {
          const parts = name.split('.');
          const index = +parts[2];
          setValue(`body.milestones.${index}.duration`, null);
          setValue(`body.milestones.${index}.end`, '');
          if (getValues(`body.milestones.${index}.recurring`)) {
            setTimeout(() => {
              trigger(`body.milestones.${index}.duration`);
            });
          } else {
            setTimeout(() => {
              trigger(`body.milestones.${index}.end`);
            });
          }
        }
        if (name.startsWith('body.milestones.') && name.endsWith('.budget')) {
          updateBudget();
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <Stack>
      <Box sx={{ mb: 5 }}>
        <Stack spacing={2}>
          {fields.map((item, index) => (
            <MilestoneItem
              key={item.id}
              index={index}
              onRemove={removeMilestone}
              disabled={disabled}
              totalMilestones={fields.length}
            />
          ))}
        </Stack>
      </Box>

      <Stack alignItems="center" justifyContent="center">
        <CustomButton
          disabled={disabled}
          onClick={() => setIsModalOpen(true)}
          variant="primaryText"
          startIcon={
            <Stack
              sx={{
                width: '24px',
                height: '24px',
                backgroundColor: 'primaryLight.500',
                borderRadius: '2px',
              }}
            >
              <AddIcon
                sx={{
                  color: '#fff',
                }}
              />
            </Stack>
          }
          sx={{
            border: `1px solid ${secondaryBlue[100]}`,
            color: 'primaryLight.500',
            bgcolor: 'primaryDark.150',
            borderRadius: '4px',
            textTransform: 'uppercase',
            fontSize: '12px',
            fontWeight: 600,
            padding: '10px 12px',
            boxShadow: `6px 6px 15px rgba(${primaryDark[600]}, 0.1)`,
            '&:hover': {
              bgcolor: 'primaryLight.100',
              borderColor: 'transparent',
            },
          }}
        >
          {t('projects.addNewMilestone')}
        </CustomButton>
      </Stack>

      <AddMilestoneModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={(newMilestones) => {
          append(newMilestones);

          // Force dirty check for body.milestones to update formState in ContractEditor
          const currentMilestones = getValues('body.milestones');
          setValue('body.milestones', currentMilestones, {
            shouldDirty: true,
            shouldValidate: true,
          });

          const addedBudget = newMilestones.reduce(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (acc: number, m: any) => acc + (m.budget || 0),
            0,
          );
          setValue('initialAmount', (watch('initialAmount') || 0) + addedBudget);
        }}
        minStartDate={(() => {
          const milestones = getValues('body.milestones');
          if (milestones && milestones.length > 0) {
            const last = milestones[milestones.length - 1];
            if (last && last.end) {
              return addDays(parseISO(last.end), 1);
            }
          }
          return null;
        })()}
        currency={watch('body.scope.currency')}
        currentProjectBudget={watch('initialAmount') || 0}
      />
    </Stack>
  );
};

export default MilestonesForm;
